import React, { createContext, useContext, useState, useCallback } from 'react';
import { PodcastEpisode } from '@/types/podcastHost';

interface PodcastEpisodeDialogContextType {
  isOpen: boolean;
  mode: 'create' | 'edit';
  editingEpisode: PodcastEpisode | null;
  showId: string | null;
  openCreateDialog: (showId: string) => void;
  openEditDialog: (episode: PodcastEpisode) => void;
  closeDialog: () => void;
}

const PodcastEpisodeDialogContext = createContext<PodcastEpisodeDialogContextType | undefined>(undefined);

export const PodcastEpisodeDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingEpisode, setEditingEpisode] = useState<PodcastEpisode | null>(null);
  const [showId, setShowId] = useState<string | null>(null);

  const openCreateDialog = useCallback((showId: string) => {
    setMode('create');
    setShowId(showId);
    setEditingEpisode(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((episode: PodcastEpisode) => {
    setMode('edit');
    setShowId(episode.show_id);
    setEditingEpisode(episode);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingEpisode(null);
    setShowId(null);
  }, []);

  return (
    <PodcastEpisodeDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingEpisode,
        showId,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </PodcastEpisodeDialogContext.Provider>
  );
};

export const usePodcastEpisodeDialog = () => {
  const context = useContext(PodcastEpisodeDialogContext);
  if (!context) {
    throw new Error('usePodcastEpisodeDialog must be used within PodcastEpisodeDialogProvider');
  }
  return context;
};
